import { useEffect } from "react";

const GENDERS = ["Female", "Male", "Other", "Prefer not to say"];

/**
 * Reusable Gender / Age / City / Location block.
 * Props:
 *   values: { gender, age, city, location }
 *   onChange: ({ gender, age, city, location }) => void
 *   dark?: boolean — dark section styling (SunGuard) vs light (Regimen)
 *   autoLocate?: boolean — auto-fill city/location from geolocation (default true)
 */
export default function DemographicsFields({ values, onChange, dark = false, autoLocate = true, tidPrefix = "demo" }) {
  const set = (k) => (e) => onChange({ ...values, [k]: e.target.value });
  const setGender = (v) => onChange({ ...values, gender: v });

  // one-time geo auto-fill for city/location if empty
  useEffect(() => {
    if (!autoLocate) return;
    if (values.city || values.location) return;
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lon } = pos.coords;
        try {
          const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&zoom=10&addressdetails=1`);
          if (!r.ok) return;
          const j = await r.json();
          const a = j.address || {};
          const city = a.city || a.town || a.village || a.suburb || a.county || "";
          const state = a.state || "";
          const loc = [a.suburb, a.city || a.town, state].filter(Boolean).join(", ") || j.display_name || `${lat.toFixed(3)}, ${lon.toFixed(3)}`;
          onChange({ ...values, city: values.city || city, location: values.location || loc });
        } catch { /* ignore */ }
      },
      () => {}, { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const labelCls = dark ? "text-[10px] tracking-[0.18em] uppercase text-white/50 block mb-1.5"
                        : "text-[10px] tracking-[0.18em] uppercase text-[color:var(--relumia-muted)] block mb-1.5";
  const inputCls = dark
    ? "w-full bg-transparent border-b border-white/25 py-2 text-[13.5px] text-white placeholder:text-white/40 focus:outline-none focus:border-[color:var(--relumia-gold)]"
    : "w-full bg-transparent border-b border-[color:var(--relumia-ink)]/25 py-2 text-[13.5px] text-[color:var(--relumia-ink)] placeholder:text-[color:var(--relumia-muted)] focus:outline-none focus:border-[color:var(--relumia-gold)]";
  const chipBase = "rounded-full px-3 py-1.5 text-[11.5px] tracking-wide border transition-colors";
  const chipOn = dark
    ? "bg-[color:var(--relumia-gold)] text-[color:var(--relumia-ink)] border-[color:var(--relumia-gold)]"
    : "bg-[color:var(--relumia-ink)] text-white border-[color:var(--relumia-ink)]";
  const chipOff = dark
    ? "bg-transparent text-white/70 border-white/25 hover:border-[color:var(--relumia-gold)]"
    : "bg-transparent text-[color:var(--relumia-ink)]/70 border-[color:var(--relumia-ink)]/20 hover:border-[color:var(--relumia-gold)]";

  return (
    <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
      <div className="sm:col-span-2">
        <label className={labelCls}>Gender</label>
        <div className="flex flex-wrap gap-1.5" data-testid={`${tidPrefix}-gender`}>
          {GENDERS.map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGender(g)}
              data-testid={`${tidPrefix}-gender-${g.split(" ")[0].toLowerCase()}`}
              className={`${chipBase} ${values.gender === g ? chipOn : chipOff}`}
            >
              {g}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className={labelCls}>Age</label>
        <input type="number" min="0" max="120" value={values.age ?? ""} onChange={set("age")} placeholder="e.g. 28" data-testid={`${tidPrefix}-age`} className={inputCls} />
      </div>
      <div>
        <label className={labelCls}>City</label>
        <input type="text" value={values.city ?? ""} onChange={set("city")} placeholder="e.g. Mumbai" data-testid={`${tidPrefix}-city`} className={inputCls} />
      </div>
      <div className="sm:col-span-2">
        <label className={labelCls}>Neighbourhood / area <span className="opacity-60">(auto-detected · you can edit)</span></label>
        <input type="text" value={values.location ?? ""} onChange={set("location")} placeholder="e.g. Mira Road East" data-testid={`${tidPrefix}-location`} className={inputCls} />
      </div>
    </div>
  );
}
